import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, SearchIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "../components/utilities/navbar";
import Footer from "../components/utilities/footer";
import { SERVICES } from "../components/utilities/config";
import { motion } from "framer-motion";
import { theme } from "../components/utilities/theme";
import { useMemo, useState } from "react";

const MotionBox = motion.create(Box);

const serviceDetails = {
  "Build Websites": {
    category: "Web",
    bestFor: "Best for launching or refreshing a brand presence.",
    highlights: ["Responsive pages", "SEO-ready structure", "Conversion-led layouts"],
  },
  "Mobile Apps": {
    category: "Apps",
    bestFor: "Best for products that need to live in users' pockets.",
    highlights: ["Android and iOS", "Smooth user flows", "Performance-focused builds"],
  },
  "Web Applications": {
    category: "Apps",
    bestFor: "Best for dashboards, SaaS platforms, and internal tools.",
    highlights: ["Scalable architecture", "Secure workflows", "Role-based experiences"],
  },
  "API Gateways": {
    category: "Systems",
    bestFor: "Best for connecting teams, tools, and data reliably.",
    highlights: ["Secure routing", "Clean documentation", "Integration planning"],
  },
  "Consultation Services": {
    category: "Strategy",
    bestFor: "Best when you need a clear technical direction before building.",
    highlights: ["Product clarity", "Tech roadmaps", "Architecture reviews"],
  },
  "Ad Creatives": {
    category: "Growth",
    bestFor: "Best for campaigns that need stronger visual performance.",
    highlights: ["Meta and Google", "Short-form creative", "A/B test variants"],
  },
  "Web Scrapers": {
    category: "Automation",
    bestFor: "Best for collecting structured data without repetitive manual work.",
    highlights: ["Reliable extraction", "Data cleanup", "Scheduled runs"],
  },
  "CRM Platforms": {
    category: "Systems",
    bestFor: "Best for sales teams that need cleaner lead and customer tracking.",
    highlights: ["Lead pipelines", "Customer records", "Actionable dashboards"],
  },
  "Desktop Applications": {
    category: "Apps",
    bestFor: "Best for tools that need offline access or native desktop power.",
    highlights: ["Windows, macOS, Linux", "Offline support", "Native workflows"],
  },
  "AI Agents": {
    category: "Automation",
    bestFor: "Best for automating support, operations, or repetitive decisions.",
    highlights: ["Task automation", "Knowledge workflows", "Human handoff paths"],
  },
};

export default function Services() {
  const {
    pageBg: servicesBg,
    cardBg,
    cardHoverBg,
    textColor,
    textSecondary,
    accentColor,
    accentCyan,
  } = theme.colors;
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const enrichedServices = useMemo(
    () =>
      SERVICES.map((service) => ({
        ...service,
        ...serviceDetails[service.name],
      })),
    []
  );

  const categories = useMemo(
    () => ["All", ...new Set(enrichedServices.map((service) => service.category))],
    [enrichedServices]
  );

  const filteredServices = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return enrichedServices.filter((service) => {
      const matchesCategory =
        activeCategory === "All" || service.category === activeCategory;
      const searchableText = [
        service.name,
        service.description,
        service.bestFor,
        service.category,
        ...service.highlights,
      ]
        .join(" ")
        .toLowerCase();

      return matchesCategory && searchableText.includes(normalizedQuery);
    });
  }, [activeCategory, enrichedServices, query]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
    hover: {
      y: -6,
      boxShadow: "0 18px 42px rgba(255, 111, 32, 0.22)",
      backgroundColor: cardHoverBg,
      transition: { duration: 0.2 },
    },
  };

  return (
    <Box bg={servicesBg} color={textColor} minH="100vh">
      <Navbar />
      <Box as="main" id="main-content" maxW="1360px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 10, md: 16 }}>
        <Stack
          direction={{ base: "column", lg: "row" }}
          align={{ base: "start", lg: "end" }}
          justify="space-between"
          spacing={8}
          mb={{ base: 8, md: 12 }}
        >
          <Box maxW="720px">
            <Badge
              bg="rgba(0, 255, 208, 0.1)"
              color={accentCyan}
              border="1px solid rgba(0, 255, 208, 0.28)"
              borderRadius="full"
              px={4}
              py={1}
              mb={4}
              letterSpacing="wide"
            >
              Services
            </Badge>
            <Heading
              as="h1"
              color={textColor}
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.08"
              fontWeight="extrabold"
              mb={5}
            >
              Find the right build path for your product.
            </Heading>
            <Text fontSize={{ base: "md", md: "xl" }} color={textSecondary} lineHeight="1.8">
              Explore Orvitrix services by goal, compare what each option is best for,
              and move straight into the details when one feels right.
            </Text>
          </Box>

          <HStack spacing={3} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={6}
              _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
              _active={{ transform: "translateY(0)" }}
            >
              Start a Project
            </Button>
            <Button
              as={RouterLink}
              to="/portfolio"
              variant="outline"
              borderColor="rgba(0, 255, 208, 0.5)"
              color={accentCyan}
              borderRadius="lg"
              px={6}
              _hover={{ bg: "rgba(0, 255, 208, 0.08)" }}
            >
              View Work
            </Button>
          </HStack>
        </Stack>

        <Flex
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={4}
          direction={{ base: "column", lg: "row" }}
          mb={8}
        >
          <InputGroup maxW={{ base: "100%", lg: "380px" }}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color={textSecondary} />
            </InputLeftElement>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by service, goal, or platform"
              bg="rgba(15, 23, 42, 0.72)"
              borderColor="rgba(255, 255, 255, 0.14)"
              borderRadius="lg"
              color={textColor}
              _placeholder={{ color: "rgba(244, 244, 246, 0.52)" }}
              _hover={{ borderColor: "rgba(0, 255, 208, 0.42)" }}
              _focus={{
                borderColor: accentCyan,
                boxShadow: "0 0 0 1px rgba(0, 255, 208, 0.55)",
              }}
            />
          </InputGroup>

          <HStack spacing={2} overflowX="auto" pb={1} w={{ base: "100%", lg: "auto" }}>
            {categories.map((category) => {
              const isActive = activeCategory === category;

              return (
                <Button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  size="sm"
                  borderRadius="full"
                  px={4}
                  flexShrink={0}
                  bg={isActive ? accentColor : "rgba(255, 255, 255, 0.06)"}
                  color={isActive ? "white" : textColor}
                  border="1px solid"
                  borderColor={isActive ? accentColor : "rgba(255, 255, 255, 0.14)"}
                  _hover={{
                    bg: isActive ? "#ff853d" : "rgba(255, 255, 255, 0.1)",
                    borderColor: isActive ? "#ff853d" : "rgba(0, 255, 208, 0.4)",
                  }}
                >
                  {category}
                </Button>
              );
            })}
          </HStack>
        </Flex>

        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          as={SimpleGrid}
          columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }}
          spacing={{ base: 5, md: 6 }}
          w="100%"
        >
          {filteredServices.map(
            ({ icon, color, name, description, path, category, bestFor, highlights }) => (
              <MotionBox
                key={name}
                as={RouterLink}
                to={path}
                p={{ base: 6, md: 7 }}
                bg={cardBg}
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                minH="100%"
                color={textColor}
                textDecoration="none"
                variants={cardVariants}
                whileHover="hover"
                whileFocus="hover"
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                display="flex"
                flexDirection="column"
                gap={5}
                cursor="pointer"
                w="100%"
                _hover={{ textDecoration: "none", borderColor: `${color}88` }}
                _focusVisible={{
                  outline: "none",
                  boxShadow: `0 0 0 3px ${color}66`,
                }}
                sx={{
                  "&:hover .service-arrow, &:focus-visible .service-arrow": {
                    transform: "translateX(4px)",
                  },
                }}
              >
                <Flex align="center" justify="space-between" gap={4}>
                  <Flex
                    align="center"
                    justify="center"
                    w={14}
                    h={14}
                    borderRadius="lg"
                    bg={`${color}1A`}
                    border="1px solid"
                    borderColor={`${color}55`}
                  >
                    <Icon as={icon} w={7} h={7} color={color} />
                  </Flex>
                  <Badge
                    color={color}
                    bg={`${color}17`}
                    border="1px solid"
                    borderColor={`${color}44`}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    {category}
                  </Badge>
                </Flex>

                <Box>
                  <Heading size="md" mb={3} color={textColor}>
                    {name}
                  </Heading>
                  <Text fontSize="sm" color={textSecondary} lineHeight="1.7">
                    {description}
                  </Text>
                </Box>

                <Text fontSize="sm" color={textColor} fontWeight="semibold" lineHeight="1.6">
                  {bestFor}
                </Text>

                <VStack align="stretch" spacing={2} flex="1">
                  {highlights.map((highlight) => (
                    <HStack key={highlight} spacing={2} align="start">
                      <Box
                        w={2}
                        h={2}
                        mt="7px"
                        borderRadius="full"
                        bg={color}
                        flexShrink={0}
                      />
                      <Text fontSize="sm" color={textSecondary}>
                        {highlight}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                <Flex
                  align="center"
                  justify="space-between"
                  pt={3}
                  borderTop="1px solid rgba(255, 255, 255, 0.1)"
                  color={color}
                  fontWeight="bold"
                  fontSize="sm"
                >
                  <Text>Explore service</Text>
                  <ArrowForwardIcon className="service-arrow" transition="transform 0.2s" />
                </Flex>
              </MotionBox>
            )
          )}
        </MotionBox>

        {filteredServices.length === 0 && (
          <Box
            textAlign="center"
            border="1px solid rgba(255, 255, 255, 0.12)"
            borderRadius="xl"
            py={12}
            px={6}
            bg="rgba(15, 23, 42, 0.72)"
          >
            <Heading size="md" mb={3}>
              No matching services found
            </Heading>
            <Text color={textSecondary} mb={5}>
              Try another search term or browse all services.
            </Text>
            <Button
              onClick={() => {
                setQuery("");
                setActiveCategory("All");
              }}
              bg={accentColor}
              color="white"
              _hover={{ bg: "#ff853d" }}
            >
              Show All Services
            </Button>
          </Box>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
